import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import LiveCaller from '@/LiveCaller'

/**
 * Centralized WebSocket Store
 *
 * Manages ALL global WebSocket channel subscriptions.
 * Initializes once on login, cleans up on logout.
 * Components subscribe to events via LiveCaller.$on('ws:EventName')
 *
 * Events emitted:
 * - ws:UserAttachedToConversation (user channel)
 * - ws:UserAttachedToConversation:account (account channel)
 * - ws:ConversationCreated
 * - ws:ConversationClosed
 * - ws:ConversationReopened
 * - ws:ParticipantDetached
 * - ws:ConversationMessageCreated
 * - ws:ServeRequestReceived
 * - ws:ServedRequestsDecremented
 * - ws:VisitorUpdated
 * - ws:ChatBotAttached
 * - ws:ChatBotTransferred
 * - ws:AiAssistantAttached
 * - ws:AiAssistantTransferred
 */
export const useWebSocketStore = defineStore('websocket', () => {
    const initialized = ref(false)
    const accountChannel = ref(null)
    const userChannel = ref(null)
    const skillChannel = ref([])
    const userId = ref(null)
    const accountId = ref(null)

    const isConnected = computed(() => initialized.value)

    /**
     * Bind conversation lifecycle listeners to a channel.
     * Emits the standard ws:* events so all consumers work the same way
     * regardless of whether events come from account or skill channels.
     */
    const bindConversationListeners = (channel) => {
        channel.listen('Conversation\\ConversationCreated', (e) => {
            LiveCaller.$emit('ws:ConversationCreated', e)
        })
        channel.listen('Conversation\\ConversationClosed', (e) => {
            LiveCaller.$emit('ws:ConversationClosed', e)
        })
        channel.listen('Conversation\\ConversationReopened', (e) => {
            LiveCaller.$emit('ws:ConversationReopened', e)
        })
        channel.listen('UserAttachedToConversation', (e) => {
            LiveCaller.$emit('ws:UserAttachedToConversation:account', e)
        })
        channel.listen('Conversation\\ParticipantDetachedFromConversation', (e) => {
            LiveCaller.$emit('ws:ParticipantDetached', e)
        })
        channel.listen('Conversation\\ConversationMessageCreated', (e) => {
            LiveCaller.$emit('ws:ConversationMessageCreated', e)
        })
        channel.listen('ChatBot\\ChatBotAttachedToConversation', (e) => {
            LiveCaller.$emit('ws:ChatBotAttached', e)
        })
        channel.listen('ChatBot\\ChatBotTransferredToOperator', (e) => {
            LiveCaller.$emit('ws:ChatBotTransferred', e)
        })
        channel.listen('AI\\AiAssistantAttachedToConversation', (e) => {
            LiveCaller.$emit('ws:AiAssistantAttached', e)
        })
        channel.listen('AI\\AiAssistantTransferredToOperator', (e) => {
            LiveCaller.$emit('ws:AiAssistantTransferred', e)
        })
    }

    /**
     * Account channel - always subscribed.
     * Conversation lifecycle listeners are always bound here since not all
     * events are broadcast on skill channels yet. Skill channels are additive.
     */
    const setupAccountChannel = () => {
        accountChannel.value = LiveCaller.echo.private(`account.${accountId.value}`)

        bindConversationListeners(accountChannel.value)

        // Non-conversation events
        accountChannel.value.listen('VisitorUpdated', (e) => {
            LiveCaller.$emit('ws:VisitorUpdated', e)
        })
        // NOTE: lc:member_updated is broadcast on presence-online-users.*,
        // not this private account channel — it's handled (and the own-user
        // availability synced) in stores/onlineUsers.js.
        accountChannel.value.listen('PBX\\DeviceStateChanged', (e) => {
            LiveCaller.$emit('ws:DeviceStateChanged', e)
        })
    }

    /**
     * Skill-based channel subscriptions (from user.skills in /v1/user response).
     * Subscribes to:
     * - account.{accountId}.channel.{channelName} for each channel skill
     * - channel-line.{channelLineId} for each channel line skill
     *
     * The backend ensures no overlap between channels and channel lines.
     */
    const setupSkillChannels = (skills) => {
        const channels = skills?.channels || []
        const channelLines = skills?.channel_lines || []

        channels.forEach((ch) => {
            const name = `account.${accountId.value}.channel.${ch.name}`
            const channel = LiveCaller.echo.private(name)
            bindConversationListeners(channel)
            skillChannel.value.push(name)
        })

        channelLines.forEach((line) => {
            const name = `channel-line.${line.id}`
            const channel = LiveCaller.echo.private(name)
            bindConversationListeners(channel)
            skillChannel.value.push(name)
        })
    }

    /**
     * User channel - user-specific events
     */
    const setupUserChannel = () => {
        userChannel.value = LiveCaller.echo.private(`users.${userId.value}`)

        // User attached to conversation (user-specific)
        userChannel.value.subscription.bind('App\\Events\\UserAttachedToConversation', (data) => {
            LiveCaller.$emit('ws:UserAttachedToConversation', data)
        })

        // Serve requests
        userChannel.value.listen('Conversation\\ConversationServeRequestReceived', (e) => {
            LiveCaller.$emit('ws:ServeRequestReceived', e)
        })

        userChannel.value.listen('Conversation\\ServedRequestsDecremented', (e) => {
            LiveCaller.$emit('ws:ServedRequestsDecremented', e)
        })

        // AI events
        userChannel.value.listen('AI\\AIMessageSuggestion', (e) => {
            LiveCaller.$emit('ws:AIMessageSuggestion', e)
        })

        // Export completed notification - user gets download URL when export is ready
        userChannel.value.notification((notification) => {
            if (notification?.type === 'App\\Notifications\\SendExportCompletedNotification') {
                LiveCaller.$emit('ws:ExportCompleted', notification)
            }
        })
    }

    /**
     * Initialize WebSocket channels - call once on login
     */
    const initialize = (user) => {
        if (initialized.value) {
            return
        }

        if (!user?.id || !user?.account_id) {
            return
        }

        userId.value = user.id
        accountId.value = user.account_id

        const { skills } = user

        setupAccountChannel()
        if (skills?.channels?.length || skills?.channel_lines?.length) {
            setupSkillChannels(skills)
        }
        setupUserChannel()

        initialized.value = true
    }

    /**
     * Cleanup - call on logout
     */
    const destroy = () => {
        if (!initialized.value) return

        if (accountChannel.value) {
            LiveCaller.echo.leave(`account.${accountId.value}`)
            accountChannel.value = null
        }

        skillChannel.value.forEach((name) => {
            LiveCaller.echo.leave(name)
        })
        skillChannel.value = []

        if (userChannel.value) {
            LiveCaller.echo.leave(`users.${userId.value}`)
            userChannel.value = null
        }

        userId.value = null
        accountId.value = null
        initialized.value = false
    }

    return {
        // State
        initialized,
        isConnected,
        userId,
        accountId,

        // Actions
        initialize,
        destroy,
    }
})

export default useWebSocketStore

